/**
 * Theme Storage Utilities
 *
 * Platform-agnostic storage abstraction for theme persistence.
 * Supports web (localStorage) and React Native (AsyncStorage).
 */

import type { IStorageAdapter, IStorageKeys, ThemeMode, IOrganizationTheme } from './types';

// ============================================================================
// STORAGE KEYS FACTORY
// ============================================================================

/**
 * Create namespaced storage keys for an app
 */
export function createStorageKeys(appId: string): IStorageKeys {
  return {
    themeMode: `tonnie-${appId}-theme-mode`,
    organizationTheme: `tonnie-${appId}-org-theme`,
  };
}

// ============================================================================
// WEB STORAGE ADAPTER
// ============================================================================

/**
 * Default web storage adapter using localStorage
 */
export const webStorageAdapter: IStorageAdapter = {
  getItem: (key: string) => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage might be full or disabled
    }
  },
  removeItem: (key: string) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  },
};

// ============================================================================
// STORAGE HELPERS
// ============================================================================

/**
 * Load theme mode from storage
 */
export async function loadThemeMode(
  adapter: IStorageAdapter,
  keys: IStorageKeys,
  defaultMode: ThemeMode
): Promise<ThemeMode> {
  try {
    const stored = await adapter.getItem(keys.themeMode);
    if (stored === 'light' || stored === 'dark' || stored === 'system' || stored === 'custom') {
      return stored;
    }
  } catch {
    // Ignore errors, return default
  }
  return defaultMode;
}

/**
 * Save theme mode to storage
 */
export async function saveThemeMode(
  adapter: IStorageAdapter,
  keys: IStorageKeys,
  mode: ThemeMode
): Promise<void> {
  try {
    await adapter.setItem(keys.themeMode, mode);
  } catch {
    // Ignore errors
  }
}

/**
 * Load organization theme from storage
 */
export async function loadOrganizationTheme(
  adapter: IStorageAdapter,
  keys: IStorageKeys
): Promise<IOrganizationTheme | undefined> {
  try {
    const stored = await adapter.getItem(keys.organizationTheme);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore errors
  }
  return undefined;
}

/**
 * Save organization theme to storage
 */
export async function saveOrganizationTheme(
  adapter: IStorageAdapter,
  keys: IStorageKeys,
  theme: IOrganizationTheme | undefined
): Promise<void> {
  try {
    if (theme) {
      await adapter.setItem(keys.organizationTheme, JSON.stringify(theme));
    } else {
      await adapter.removeItem(keys.organizationTheme);
    }
  } catch {
    // Ignore errors
  }
}

/**
 * Clear all theme storage for an app
 */
export async function clearThemeStorage(
  adapter: IStorageAdapter,
  keys: IStorageKeys
): Promise<void> {
  try {
    await adapter.removeItem(keys.themeMode);
    await adapter.removeItem(keys.organizationTheme);
  } catch {
    // Ignore errors
  }
}
